import React, { useEffect, useState } from "react";
import { ThemeProvider } from "@mui/material/styles";

import AdapterDateFns from "@mui/lab/AdapterDateFns";
import AngleCirculeLeft from "shared/components/utils/icons/AngleCirculeLeft";
import AngleCirculeRight from "shared/components/utils/icons/AngleCirculeRight";
import Box from "@mui/material/Box";
import Calendar from "shared/components/utils/icons/Calendar";
import DatePicker from "@mui/lab/DatePicker";
import DownCaret from "shared/components/utils/icons/DownCaret";
import LocalizationProvider from "@mui/lab/LocalizationProvider";
import PickersDay from "@mui/lab/PickersDay";
import PropTypes from "prop-types";
import Tooltip from "shared/components/utils/tooltip/Tooltip";
import endOfWeek from "date-fns/endOfWeek";
import isSameDay from "date-fns/isSameDay";
import isWithinInterval from "date-fns/isWithinInterval";
import locale from "date-fns/locale/en-US";
import moment from "moment";
import startOfWeek from "date-fns/startOfWeek";
import { CustomPickersDay, DateLabel, theme, useStyles } from "./WeekPicker.styles";

function getCurrentWeek(currentWeekDate, weekStartOnMonday) {
    const start = startOfWeek(moment().toDate(), weekStartOnMonday);
    const end = endOfWeek(moment().toDate(), weekStartOnMonday);

    return getWeekProps(currentWeekDate, start, end);
}

function getWeekProps(weekDate, start, end) {
    const dayIsBetween = isWithinInterval(weekDate, { start, end });
    const isFirstDay = isSameDay(weekDate, start);
    const isLastDay = isSameDay(weekDate, end);

    return { dayIsBetween, isFirstDay, isLastDay };
}

export default function WeekPicker({
    selectedValue,
    dateFull,
    dateLabel,
    onChangeWeek,
    minDate,
    maxDate,
}) {
    const [value, setValue] = useState(moment().toDate());
    const [isPickerOpen, setIsPickerOpen] = useState(false);
    const classes = useStyles();
    const weekStartOnMonday = { weekStartsOn: 1 };
    let monthYearPickerOpen = false;

    if (locale?.options) {
        locale.options.weekStartsOn = 1;
    }

    useEffect(() => {
        if (moment(selectedValue).isValid()) {
            setValue(selectedValue);
        }
    }, [selectedValue]);

    const renderWeekPickerDay = (date, _selectedDates, pickersDayProps) => {
        if (!value) {
            return <PickersDay key={date.toString()} {...pickersDayProps} />;
        }

        const selectedWeekProps = getSelectedWeek(date);
        const currentWeekProps = getCurrentWeek(date, weekStartOnMonday);

        return (
            <CustomPickersDay
                key={date.toString()}
                {...pickersDayProps}
                disableMargin
                selectedWeekProps={selectedWeekProps}
                currentWeekProps={currentWeekProps}
                sx={{ width: "2.5rem" }}
            />
        );

        function getSelectedWeek(selectedWeekDate) {
            const start = startOfWeek(value, weekStartOnMonday);
            const end = endOfWeek(value, weekStartOnMonday);

            return getWeekProps(selectedWeekDate, start, end);
        }

    };
    return (
        <ThemeProvider theme={theme}>
            <LocalizationProvider dateAdapter={AdapterDateFns} locale={locale}>
                <DatePicker
                    displayStaticWrapperAs="desktop"
                    label="Week picker"
                    views={["year", "month", "day"]}
                    value={value}
                    onChange={(newValue) => {
                        if (monthYearPickerOpen) {
                            return;
                        }
                        setValue(startOfWeek(newValue, weekStartOnMonday));
                        onChangeWeek(startOfWeek(newValue, weekStartOnMonday));
                    }}
                    renderDay={renderWeekPickerDay}
                    onClick={() => setIsPickerOpen(true)}
                    open={isPickerOpen}
                    onClose={() => {
                        setIsPickerOpen(false);
                    }}
                    PopperProps={{ placement: "bottom-start" }}
                    renderInput={({ inputRef, _inputProps, InputProps }) => (
                        <Tooltip placement="bottom" title={dateFull}>
                            <Box
                                sx={{ display: "flex", alignItems: "center" }}
                                className={classes.button}
                                onClick={() => setIsPickerOpen(true)}
                            >
                                <DateLabel ref={inputRef} style={{ fontSize: "0.9rem" }}>
                                    {" "}
                                    {dateLabel}{" "}
                                </DateLabel>
                            </Box>
                        </Tooltip>
                    )}
                    inputFormat="MMM dd"
                    components={{
                        OpenPickerIcon: Calendar,
                        RightArrowIcon: AngleCirculeRight,
                        LeftArrowIcon: AngleCirculeLeft,
                        SwitchViewIcon: DownCaret,
                    }}
                    onMonthChange={(_x) => setValue((prev) => prev)}
                    onYearChange={(_x) => setValue((prev) => prev)}
                    onViewChange={(x) => { monthYearPickerOpen = x === "year" || x === "month"; }}
                    minDate={minDate}
                    maxDate={maxDate}
                    showDaysOutsideCurrentMonth={true}
                />
            </LocalizationProvider>
        </ThemeProvider>
    );
}

WeekPicker.propTypes = {
    dateLabel: PropTypes.string,
    onChangeWeek: PropTypes.func,
    minDate: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
    maxDate: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
    initialValue: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
    selectedValue: PropTypes.oneOfType([PropTypes.string, PropTypes.object, PropTypes.instanceOf(Date)]),
    dateFull: PropTypes.string,
};
